import * as React from "react";
import * as THREE from "three";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { suspend } from "suspend-react";
import Hls, { Events } from "hls.js";

const IS_BROWSER = (() =>
  typeof window !== "undefined" &&
  typeof window.document?.createElement === "function" &&
  typeof window.navigator?.userAgent === "string")();

let _HLSModule = null;
async function getHls(...args) {
  _HLSModule ??= await import("hls.js"); // singleton
  const Ctor = _HLSModule.default;
  if (Ctor.isSupported()) {
    return new Ctor(...args);
  }
  return null;
}

// Create a 1x1 transparent placeholder texture for error cases
function createPlaceholderTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "rgba(0,0,0,0)";
  ctx.fillRect(0, 0, 1, 1);
  return new THREE.CanvasTexture(canvas);
}

export function useVideoTexture(
  srcOrSrcObject,
  {
    unsuspend = "loadedmetadata",
    start = true,
    hls: hlsConfig = {},
    crossOrigin = "anonymous",
    muted = true,
    loop = true,
    playsInline = true,
    fallbackSrc,
    onVideoFrame,
    ...videoProps
  } = {}
) {
  const gl = useThree((state) => state.gl);
  const hlsRef = useRef(null);
  const didFallbackRef = useRef(false);

  // No provider-specific fallback; rely on caller to supply fallbackSrc

  const texture = suspend(
    () =>
      new Promise(async (res) => {
        let src;
        let srcObject;
        if (typeof srcOrSrcObject === "string") {
          src = srcOrSrcObject;
        } else {
          srcObject = srcOrSrcObject;
        }

        // Early return placeholder for invalid/empty sources
        if (!src && !srcObject) {
          console.warn("[VideoTexture] No valid source provided");
          res(createPlaceholderTexture());
          return;
        }

        const video = Object.assign(document.createElement("video"), {
          src,
          srcObject,
          crossOrigin,
          loop,
          muted,
          playsInline,
          ...videoProps,
        });

        // Ensure inline, muted autoplay for iOS and Low Power Mode

        // Explicitly set attributes as some browsers gate behavior on attribute presence
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.setAttribute("muted", "");
        video.setAttribute("autoplay", "");

        // Mirror via properties as well
        video.playsInline = true;
        video.muted = true;
        video.defaultMuted = true;
        video.autoplay = true;

        // Prefer auto to avoid black frames before first play
        video.preload = "auto";

        // Reduce likelihood of remote casting / external player
        video.disableRemotePlayback = true;
        video.controls = false;

        // Track if we've already resolved to prevent multiple resolutions
        let hasResolved = false;
        const resolveOnce = (texture) => {
          if (hasResolved) return;
          hasResolved = true;
          res(texture);
        };

        // Safety timeout - if nothing loads within 15 seconds, resolve with placeholder
        const safetyTimeout = setTimeout(() => {
          if (!hasResolved) {
            console.warn("[VideoTexture] Safety timeout reached, using placeholder for:", src);
            if (hlsRef.current) {
              try { hlsRef.current.destroy(); } catch (_) {}
              hlsRef.current = null;
            }
            resolveOnce(createPlaceholderTexture());
          }
        }, 15000);

        const texture = new THREE.VideoTexture(video);
        texture.colorSpace = gl.outputColorSpace;

        // Final error handler - when all fallbacks have failed
        const onFatalError = () => {
          console.warn("[VideoTexture] All sources failed, using placeholder for:", src);
          clearTimeout(safetyTimeout);
          if (hlsRef.current) {
            try { hlsRef.current.destroy(); } catch (_) {}
            hlsRef.current = null;
          }
          resolveOnce(createPlaceholderTexture());
        };

        // HTMLVideoElement error -> switch to provided fallbackSrc (if any)
        const onVideoError = (e) => {
          // If we already tried the fallback and that also failed, give up gracefully
          if (didFallbackRef.current) {
            onFatalError();
            return;
          }
          
          if (fallbackSrc) {
            didFallbackRef.current = true;

            if (hlsRef.current) {
              try { hlsRef.current.destroy(); } catch (_) {}
              hlsRef.current = null;
            }

            video.src = fallbackSrc;
            video.load();
          } else {
            // No fallback available, resolve with placeholder
            onFatalError();
          }
        };

        video.addEventListener("error", onVideoError);
        video.addEventListener(unsuspend, () => {
          clearTimeout(safetyTimeout);
          resolveOnce(texture);
        }, { once: true });

        // hlsjs extension
        if (src && IS_BROWSER && src.endsWith(".m3u8")) {
          const hls = (hlsRef.current = await getHls(hlsConfig));
          if (hls) {
            const onHlsError = (_, data) => {
              // Attempt automatic recovery for media errors; fallback for fatal/network errors
              if (data?.fatal) {
                try {
                  hls.destroy();
                } catch (_) {}
                hlsRef.current = null;
                
                if (fallbackSrc && !didFallbackRef.current) {
                  didFallbackRef.current = true;
                  video.src = fallbackSrc;
                  // Let loadedmetadata resolve the promise
                  video.load();
                } else {
                  // No fallback or fallback already tried - resolve with placeholder
                  onFatalError();
                }
              }
            };

            hls.on(Events.ERROR, onHlsError);
            hls.on(Events.MEDIA_ATTACHED, () => void hls.loadSource(src));
            hls.attachMedia(video);
          }
        }

        // Attempt playback as soon as the browser deems it can
        const tryPlay = () => {
          if (!start) return;
          const p = video.play?.();
          if (p && typeof p.catch === "function") p.catch(() => {});
        };
        video.addEventListener("canplay", tryPlay, { once: true });
        // Cleanup listeners when suspense scope unmounts (resolve handled by once:true)
        video.addEventListener(
          "loadeddata",
          () => {
            video.removeEventListener("error", onVideoError);
          },
          { once: true }
        );
      }),
    [srcOrSrcObject]
  );

  // Safely access video element - may be null for placeholder textures
  const video = texture.source?.data;
  useVideoFrame(video, onVideoFrame);

  useEffect(() => {
    // Only attempt to play if there's a video element with a play method
    if (start && texture.image?.play) {
      const p = texture.image.play();
      if (p?.catch) p.catch(() => {});
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [texture, start]);

  return texture;
}

//
// VideoTexture
//

export const VideoTexture = forwardRef(({ children, src, ...config }, fref) => {
  const texture = useVideoTexture(src, config);

  useEffect(() => {
    return () => void texture.dispose();
  }, [texture]);

  useImperativeHandle(fref, () => texture, [texture]); // expose texture through ref

  return <>{children?.(texture)}</>;
});

// rVFC hook

const useVideoFrame = (video, f) => {
  useEffect(() => {
    if (!f) return;
    if (!video || !video.requestVideoFrameCallback) return;

    let handle;
    const callback = (...args) => {
      f(...args);
      handle = video.requestVideoFrameCallback(callback);
    };
    video.requestVideoFrameCallback(callback);

    return () => video?.cancelVideoFrameCallback?.(handle);
  }, [video, f]);
};
